<template>
  <div>
    <!-- MODO “TELA NORMAL”: formulário + botão -->
    <div v-if="!printMode">
      <h6>Receitas Recebidas × Despesas Liquidadas</h6><p></p>
      <div class="row mb-3">
        <div class="col-auto">
          <label>Data Início:</label>
          <input type="date" v-model="dataInicioLocal" class="form-control" />
        </div>
        <div class="col-auto">
          <label>Data Fim:</label>
          <input type="date" v-model="dataFimLocal" class="form-control" />
        </div>
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary">Executar</button>
        </div>
      </div>
    </div>

    <!-- MODO “IMPRESSÃO/PRINT”: exibe duas tabelas -->
    <div v-else>
      <!-- Subtítulo geral (período) -->
      <h6 class="text-center mb-3" style="font-size: 14px;">
        {{ props.subtitle }}
      </h6>

      <div v-if="loading">Carregando…</div>
      <div v-else>
        <!-- Tabela de Receitas Recebidas -->
        <div v-if="receitas.length">
          <h6>Receitas Recebidas</h6>
          <table class="table table-striped mb-4">
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Pagamento</th>
                <th>Prev. Receb.</th>
                <th class="text-right">Valor</th>
                <th>Refil – Plano de Contas</th>
                <th>Descrição</th>
                <th class="text-center">Forma de Pagamento</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in receitas" :key="`rec-${idx}`">
                <td>{{ globalUtils.formatarData(item.vencimento) }}</td>
                <td>{{ globalUtils.formatarData(item.pagamento) }}</td>
                <td>{{ globalUtils.formatarData(item.recebimento) }}</td>
                <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
                <td>{{ item.refil }}</td>
                <td>{{ item.descricao }}</td>
                <td class="text-center">{{ item.forma_pagamento }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2"><strong>Total Receitas</strong></td>
                <td class="text-right">
                  <strong>{{ formatarMoeda(totalReceitas) }}</strong>
                </td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else>
          <p class="text-muted">Nenhuma receita encontrada para este período.</p>
        </div>

        <!-- Tabela de Despesas Liquidadas -->
        <div v-if="despesas.length">
          <h6>Despesas Liquidadas</h6>
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Vencimento</th>
                <th>Pagamento</th>
                <th class="text-right">Valor</th>
                <th>Refil – Plano de Contas</th>
                <th>Descrição</th>
                <th>Forma de Pagamento</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in despesas" :key="`desp-${idx}`">
                <td>{{ globalUtils.formatarData(item.vencimento) }}</td>
                <td>{{ globalUtils.formatarData(item.pagamento) }}</td>
                <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
                <td>{{ item.refil }}</td>
                <td>{{ item.descricao }}</td>
                <td>{{ item.forma_pagamento }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2"><strong>Total Despesas</strong></td>
                <td class="text-right">
                  <strong>{{ formatarMoeda(totalDespesas) }}</strong>
                </td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else>
          <p class="text-muted">Nenhuma despesa encontrada para este período.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

// Props para “print mode” e parâmetros enviados pela rota
const props = defineProps({
  printMode:  { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  printTitle: { type: String, default: 'Relatório' },
  subtitle:   { type: String, default: '' }
})

const router = useRouter()

// Estados locais (modo “tela normal”)
const dataInicioLocal = ref('')
const dataFimLocal    = ref('')

// Estados de resultado (modo “print”)
const loading         = ref(false)
const receitas        = ref([])
const despesas        = ref([])
const totalReceitas   = ref(0)
const totalDespesas   = ref(0)

// Ao entrar em printMode, busca imediatamente os dados
onMounted(async () => {
  if (!props.printMode) return
  if (!props.dataInicio || !props.dataFim) return

  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/financeiros/receitas-despesas', {
      params: {
        dataInicio: props.dataInicio,
        dataFim:    props.dataFim
      }
    })
    // Preenche arrays e totais retornados pelo backend
    receitas.value      = Array.isArray(res.data.receitas)      ? res.data.receitas      : []
    totalReceitas.value = parseFloat(res.data.totalReceitas)  || 0

    despesas.value      = Array.isArray(res.data.despesas)      ? res.data.despesas      : []
    totalDespesas.value = parseFloat(res.data.totalDespesas)  || 0
  } catch (e) {
    console.error('Erro ao buscar receitas e despesas (print):', e)
    receitas.value      = []
    totalReceitas.value = 0
    despesas.value      = []
    totalDespesas.value = 0
  } finally {
    loading.value = false
  }
})

// Função chamada ao clicar em “Executar” no modo normal
function abrirPrint() {
  if (!dataInicioLocal.value || !dataFimLocal.value) return

  const inicioFormatado = globalUtils.formatarData(dataInicioLocal.value)
  const fimFormatado    = globalUtils.formatarData(dataFimLocal.value)

  // Montar o subtitle completo: ( dd/MM/yyyy a dd/MM/yyyy )
  const subtitle = `( ${inicioFormatado} a ${fimFormatado} )`

  router.push({
    path: `/gestao/relatorios/print/receitas-despesas`,
    query: {
      title:      'Receitas Recebidas x Despesas Liquidadas',
      dataInicio: dataInicioLocal.value,
      dataFim:    dataFimLocal.value,
      subtitle:   subtitle
    }
  })
}

// Formatação de valor como moeda BRL
function formatarMoeda(num) {
  if (num == null) return ''
  return Number(num).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.text-right {
  text-align: right !important;
}
</style>
